import type { DragEndEvent } from '@dnd-kit/core'
import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { AlertTriangle, GripVertical, Plus, X } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '~/components/ui/button'
import { Input } from '~/components/ui/input'
import { cn } from '~/lib/utils'

export type ReadyModule = { id: string, title: string }

/** Un module à sa place dans le parcours en cours d'édition. */
export type PathStep = {
  moduleId: string
  /** 'YYYY-MM-DD' ou '' — le format d'un <input type="date">. */
  unlockAt: string
}

type IProps = {
  modules: ReadyModule[]
  value: PathStep[]
  onChange: (steps: PathStep[]) => void
  /** Bornes du parcours, pour signaler les dates incohérentes. */
  startDate: string
  endDate: string
}

/**
 * Regroupe les étapes consécutives partageant la même date d'ouverture.
 *
 * C'est toute l'implémentation de la notion de « palier » : le client raisonne
 * par vagues (« ces deux modules le 1er, ces trois-là le 15 »), mais le palier
 * n'est PAS une entité — juste des modules qui partagent une date (ADR 0002,
 * D4). Une table de plus n'apporterait qu'un nom de palier, que personne n'a
 * demandé.
 */
function groupIntoStages(steps: PathStep[]): { unlockAt: string, steps: PathStep[] }[] {
  const stages: { unlockAt: string, steps: PathStep[] }[] = []
  for (const step of steps) {
    const last = stages.at(-1)
    if (last && last.unlockAt === step.unlockAt) last.steps.push(step)
    else stages.push({ unlockAt: step.unlockAt, steps: [step] })
  }
  return stages
}

function SortableStep({
  step,
  title,
  index,
  warning,
  onRemove,
  onDateChange,
}: {
  step: PathStep
  title: string
  index: number
  warning?: string
  onRemove: () => void
  onDateChange: (value: string) => void
}) {
  const { t } = useTranslation()
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: step.moduleId,
  })

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        'flex flex-wrap items-center gap-2 rounded-md border border-border bg-background p-2',
        isDragging && 'z-10 opacity-80 shadow-lg',
      )}
    >
      <button
        type="button"
        className="cursor-grab touch-none rounded p-1 text-muted-foreground hover:bg-muted active:cursor-grabbing"
        aria-label={t('sessions.path.reorder', { title })}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="size-4" />
      </button>

      <span className="w-6 shrink-0 text-center text-sm tabular-nums text-muted-foreground">
        {index + 1}
      </span>
      <span className="min-w-0 flex-1 truncate text-sm font-medium">{title}</span>

      <div className="flex items-center gap-1.5">
        <label className="text-xs text-muted-foreground" htmlFor={`unlock-${step.moduleId}`}>
          {t('sessions.path.opensOn')}
        </label>
        <Input
          id={`unlock-${step.moduleId}`}
          type="date"
          className="h-8 w-[9.5rem]"
          value={step.unlockAt}
          onChange={e => onDateChange(e.target.value)}
        />
      </div>

      <Button
        type="button"
        variant="ghost"
        className="size-8 shrink-0 p-0"
        onClick={onRemove}
        aria-label={t('sessions.path.remove', { title })}
      >
        <X className="size-4" />
      </Button>

      {warning && (
        <p className="flex w-full items-center gap-1.5 text-xs text-amber-600 dark:text-amber-500">
          <AlertTriangle className="size-3.5 shrink-0" />
          {warning}
        </p>
      )}
    </div>
  )
}

/**
 * Composition d'un parcours : quels modules, dans quel ordre, et à quelle date
 * chacun s'ouvre.
 *
 * L'ordre se règle au glisser-déposer, et la POSITION n'est jamais saisie : elle
 * se déduit de l'ordre de la liste, seule source d'ordre côté API comme ici.
 */
function ModulePathBuilder({ modules, value, onChange, startDate, endDate }: IProps) {
  const { t } = useTranslation()
  const [toAdd, setToAdd] = useState('')

  const byId = new Map(modules.map(m => [m.id, m]))
  const available = modules.filter(m => !value.some(s => s.moduleId === m.id))
  const stages = groupIntoStages(value)

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return
    const from = value.findIndex(s => s.moduleId === active.id)
    const to = value.findIndex(s => s.moduleId === over.id)
    if (from === -1 || to === -1) return
    onChange(arrayMove(value, from, to))
  }

  const addModule = (moduleId: string) => {
    if (!moduleId || value.some(s => s.moduleId === moduleId)) return
    onChange([...value, { moduleId, unlockAt: '' }])
    setToAdd('')
  }

  const setDate = (moduleId: string, unlockAt: string) => {
    onChange(value.map(s => (s.moduleId === moduleId ? { ...s, unlockAt } : s)))
  }

  /** Applique une date à tout un palier — « ces trois modules le 15 », en un geste. */
  const setStageDate = (stageSteps: PathStep[], unlockAt: string) => {
    const ids = new Set(stageSteps.map(s => s.moduleId))
    onChange(value.map(s => (ids.has(s.moduleId) ? { ...s, unlockAt } : s)))
  }

  /**
   * Une date hors des bornes du parcours n'est pas refusée ici — l'API ne
   * refuse que celles postérieures à la fin, qui rendraient le module
   * inouvrable. Le reste est signalé sans bloquer : une date antérieure au
   * début est redondante, pas fausse (le verrou effectif est le plus tardif des
   * deux), et une date passée est légitime quand on rattrape une promo lancée.
   */
  const warningFor = (unlockAt: string): string | undefined => {
    if (!unlockAt) return undefined
    if (endDate && unlockAt > endDate) return t('sessions.path.warn.afterEnd')
    if (startDate && unlockAt < startDate) return t('sessions.path.warn.beforeStart')
    return undefined
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <label className="text-sm font-medium">{t('sessions.path.title')}</label>
        <span className="text-xs text-muted-foreground">
          {t('sessions.path.count', { count: value.length })}
        </span>
      </div>

      {value.length === 0
        ? (
            <p className="rounded-md border border-dashed border-border p-4 text-center text-sm text-muted-foreground">
              {t('sessions.path.empty')}
            </p>
          )
        : (
            // Pas de `modifiers` pour contraindre le glissé à l'axe vertical :
            // `@dnd-kit/modifiers` n'est pas installé, et la stratégie de liste
            // verticale suffit. Une dépendance de plus pour ce confort-là ne se
            // justifie pas.
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={value.map(s => s.moduleId)}
                strategy={verticalListSortingStrategy}
              >
                <div className="flex flex-col gap-3">
                  {stages.map(stage => (
                    <div key={`${stage.unlockAt}-${stage.steps[0]!.moduleId}`} className="flex flex-col gap-1.5">
                      {/* En-tête de palier : n'apparaît que si la vague porte
                          plusieurs modules — sur un module isolé, il ferait
                          doublon avec sa propre date. */}
                      {stage.steps.length > 1 && (
                        <div className="flex items-center gap-2 px-1">
                          <span className="text-xs font-medium text-muted-foreground">
                            {stage.unlockAt
                              ? t('sessions.path.stage.on', { count: stage.steps.length })
                              : t('sessions.path.stage.immediate', { count: stage.steps.length })}
                          </span>
                          <Input
                            type="date"
                            className="h-7 w-[9.5rem]"
                            value={stage.unlockAt}
                            onChange={e => setStageDate(stage.steps, e.target.value)}
                            aria-label={t('sessions.path.stage.setDate')}
                          />
                        </div>
                      )}
                      {stage.steps.map(step => (
                        <SortableStep
                          key={step.moduleId}
                          step={step}
                          index={value.indexOf(step)}
                          title={byId.get(step.moduleId)?.title ?? step.moduleId}
                          warning={warningFor(step.unlockAt)}
                          onRemove={() => onChange(value.filter(s => s.moduleId !== step.moduleId))}
                          onDateChange={v => setDate(step.moduleId, v)}
                        />
                      ))}
                    </div>
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}

      {available.length > 0 && (
        <div className="flex items-center gap-2">
          <select
            value={toAdd}
            onChange={e => setToAdd(e.target.value)}
            className="h-9 flex-1 rounded-md border border-border bg-transparent px-3 text-sm"
            aria-label={t('sessions.path.add')}
          >
            <option value="">{t('sessions.selectModule')}</option>
            {available.map(m => <option key={m.id} value={m.id}>{m.title}</option>)}
          </select>
          <Button type="button" variant="outline" disabled={!toAdd} onClick={() => addModule(toAdd)}>
            <Plus className="mr-2 size-4" />
            {t('sessions.path.add')}
          </Button>
        </div>
      )}
    </div>
  )
}

export default ModulePathBuilder
